import { DEG, REQS, reqByKey, angDiff } from './constants.js';
import { insideSite, ptInPoly, wallIntersection, blocked } from './geometry.js';
import { camGeom, cameraViews, inCameraFrame, pxAt } from './optics.js';
import { sceneWalls, openingCentre } from './siteGeometry.js';
import { generalKey, needsFace, threshold } from './standards.js';
import { areaAt } from './siteAreas.js';

// Inference limits and a design assumption, not an identification standard.
export const ENTRY_FACE_ANGLE = 45;
const MIN_GAP = 0.6, MAX_GAP = 5;

function requirementAt(S, x, y) {
  const requirements = S.zones.filter(z => ptInPoly(x, y, z.pts)).map(z => z.req);
  const area = areaAt(S, x, y);
  if (area?.req) requirements.push(area.req);
  if (!requirements.length) requirements.push(generalKey(S));
  return requirements.includes('identify') ? 'identify' : requirements.sort((a, b) => threshold(S, b) - threshold(S, a))[0];
}

/** Infer walk-through openings from free ends of standing walls. The extension
 * must meet another solid wall within door/gate width. This also handles doors
 * beside corners. Glazing and counters close a passage even if optically clear.
 * Nothing is added to the saved plan; geometry changes invalidate the inference. */
export function identificationEntries(S) {
  if (generalKey(S) !== 'identify' && !S.zones.some(z => z.req === 'identify') && !S.areas.some(a => a.req === 'identify')) return [];
  const gaps = [];
  // Explicit doors and passages carry the same directional obligation as a
  // wall break. A linked capture task owns that doorway's more specific goals.
  for (const o of S.openings || []) {
    if (!['door', 'passage'].includes(o.kind) || (S.tasks || []).some(t => t.enabled !== false && t.openingId === o.id)) continue;
    const p = openingCentre(S, o);
    if (p && o.width >= MIN_GAP && (o.height || 2.1) >= 1.8) gaps.push({ ...p, width: o.width, openingId: o.id, name: o.name });
  }
  const passageWalls = sceneWalls({ ...S, sceneState: 'design' }).filter(w => !w.objectKind);
  for (const wall of S.walls) {
    if (wall.kind !== 'solid' || wall.h < 1.8) continue;
    const len = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
    if (len < 0.1) continue;
    for (const sign of [-1, 1]) {
      const x = sign === 1 ? wall.x2 : wall.x1, y = sign === 1 ? wall.y2 : wall.y1;
      const ux = (wall.x2 - wall.x1) / len * sign, uy = (wall.y2 - wall.y1) / len * sign;
      let nearest = null;
      for (const other of S.walls) {
        if (other === wall) continue;
        const hit = wallIntersection(x, y, 0.8, x + ux * MAX_GAP, y + uy * MAX_GAP, 0.8, other);
        if (hit && (!nearest || hit.enter < nearest.t)) nearest = { t: hit.enter, wall: other };
      }
      if (!nearest || nearest.wall.kind !== 'solid' || nearest.wall.h < 1.8) continue;
      const width = nearest.t * MAX_GAP;
      if (width < MIN_GAP || width > MAX_GAP) continue;
      const cx = x + ux * width / 2, cy = y + uy * width / 2;
      if (gaps.some(g => Math.hypot(g.x - cx, g.y - cy) < 0.15 && Math.abs(g.ux * ux + g.uy * uy) > 0.98)) continue;
      gaps.push({ x: cx, y: cy, ux, uy, width });
    }
  }
  gaps.sort((a, b) => a.x - b.x || a.y - b.y);
  const entries = [];
  for (const [index, gap] of gaps.entries()) for (const sign of [-1, 1]) {
    const nx = -gap.uy * sign, ny = gap.ux * sign, heading = (Math.atan2(ny, nx) * DEG + 360) % 360;
    const points = [];
    // Three positions across the passage, just inside each applicable Identify
    // area. Interior openings can require views for both directions of travel.
    for (const across of [-0.3, 0, 0.3]) {
      const ax = gap.x + gap.ux * gap.width * across, ay = gap.y + gap.uy * gap.width * across;
      for (const depth of [0.5, 1, 1.5]) {
        const x = ax + nx * depth, y = ay + ny * depth;
        if (!insideSite(S, x, y) || requirementAt(S, x, y) !== 'identify') continue;
        if (passageWalls.some(w => wallIntersection(ax - nx * 0.2, ay - ny * 0.2, 0.8, x, y, 0.8, w))) continue;
        points.push({ x, y, heading }); break;
      }
    }
    if (!points.length) continue;
    const x = points.reduce((n, p) => n + p.x, 0) / points.length, y = points.reduce((n, p) => n + p.y, 0) / points.length;
    entries.push({ id: `entry:${gap.x.toFixed(2)}:${gap.y.toFixed(2)}:${heading.toFixed(0)}`, name: `${gap.name || `Opening ${index + 1}`} · entering ${Math.round(heading)}°`, x, y, heading, gap, points });
  }
  return entries.filter(e => !(S.dismissedEntries || []).includes(e.id) && !(S.openings || []).some(o => {
    const p = openingCentre(S, o);
    return !e.gap.openingId && (o.inferredId === e.id || (p && Math.hypot(p.x - e.gap.x, p.y - e.gap.y) < 0.2));
  }));
}

export function entrySample(S, cam, point, g = camGeom(S, cam)) {
  if (!g) return false;
  if (g.m.details?.sensorModules?.length) return cameraViews(S, cam).some(v => entrySample(S, v.cam, point, v.g));
  const dx = cam.x - point.x, dy = cam.y - point.y, d = Math.hypot(dx, dy);
  if (angDiff(Math.atan2(dy, dx) * DEG, point.heading) > (S.opt.faceYaw ?? ENTRY_FACE_ANGLE)) return false;
  if (Math.atan2(Math.max(cam.h - S.opt.hFace, 0), Math.max(d, 0.01)) * DEG > S.opt.aFace) return false;
  if (pxAt(g, Math.hypot(d, cam.h - S.opt.hFace)) < S.opt.t.identify) return false;
  return inCameraFrame(cam, g, point.x, point.y, S.opt.hFace) && !blocked(cam.x, cam.y, cam.h, point.x, point.y, S.opt.hFace, sceneWalls(S));
}

export function assessEntries(S, entries = identificationEntries(S)) {
  const cameras = S.cams.map(cam => ({ cam, g: camGeom(S, cam) }));
  return entries.map(entry => {
    const samples = entry.points.map(p => ({ ...p, cameras: cameras.filter(c => entrySample(S, c.cam, p, c.g)).map(c => c.cam.id) }));
    const met = samples.filter(p => p.cameras.length).length;
    return { ...entry, samples, met, total: samples.length, complete: met === samples.length };
  });
}
